import { ChevronUpIcon, ChevronDownIcon } from "vue-feather-icons";
import { TrashEventsService } from "@/services/trashEventsApi";
import TrackedItemCardActions from "./trackedItemCardActions/trackedItemCardActions.vue";
import TrackedItemCardInsights from "./trackedItemCardInsights/trackedItemCardInsights.vue";
import TrashEventItem from "@/components/topWastedItemsTrashEvents/trashEventItem.vue";
import SvgRenderer from "@/components/svgRenderer/svgRenderer.vue";
import TrackedItemCardGraph from "./trackedItemCardGraph/trackedItemCardGraph.vue";
import SetMonthlyGoalModal from "@/components/setMonthlyGoalModal/setMonthlyGoalModal.vue";
import { createNamespacedHelpers } from "vuex";
import { REQUEST_STATE, STATUS_CODE } from "@/enums";
import moment from "moment";
import addRemoveTrackedItem from "@/components/addRemoveTrackedItem/addRemoveTrackedItem.vue";
import { SettingsService } from "@/services/settingsApi";
import { createTooltipContent } from "@/utils";

const { mapGetters: brandingGetters } = createNamespacedHelpers("branding"),
	{ mapGetters: userGetters, mapActions: userActions } = createNamespacedHelpers("user");

export default {
	props: { item: Object },

	components: {
		TrackedItemCardGraph,
		SvgRenderer,
		TrashEventItem,
		ChevronUpIcon,
		ChevronDownIcon,
		TrackedItemCardActions,
		TrackedItemCardInsights,
		SetMonthlyGoalModal,
		addRemoveTrackedItem,
	},

	data() {
		return {
			REQUEST_STATE,
			state: REQUEST_STATE.LOADING,
			cardData: null,
			eventsLoading: true,
			trashEvents: null,
			seeMoreToggled: false,
			monthlyGoalModalData: null,
			showRemoveModal: false,
		};
	},

	methods: {
		openSetMonthlyGoalModal,
		closeSetMonthlyGoalModal,
		fetchData,
		fetchTrashevents,
		formatDate,
		toggleSeeMore,
		stopTrackingLabel,
		...userActions(["getMe"]),
		updateNote,
		createTooltipContent,
		equalizeBoxWidths() {
			// ensure the width of the two gray cards (WPG, CPG) are the same
			const boxes = Array.from(this.$refs["same-width"]?.children || []),
				widths = boxes.map((box) => box.offsetWidth),
				anyWidthDiff = widths.every((width) => width === widths[0]),
				maxWidth = Math.max(...widths);

			if (!anyWidthDiff) {
				boxes.forEach((box) => {
					box.style.width = maxWidth + "px";
				});
			}
		},
	},

	updated() {
		this.equalizeBoxWidths();
	},

	computed: {
		...brandingGetters(["colors"]),
		...userGetters(["activeOutlet"]),
		itemToRemove() {
			return {
				label_id: this.item.label_id,
				start_date: this.$moment(this.item.start_date, "DD.MM.YYYY"),
				translations: this.item.description,
			};
		},
	},

	mounted() {
		this.fetchData();
	},
};

function openSetMonthlyGoalModal(label) {
	const PERCENT = 100;

	this.monthlyGoalModalData = {
		monthlyGoal: this.cardData.monthly_goal * PERCENT,
		baselineWeight: this.cardData.baseline.wpg,
		label: {
			labelId: label.label_id,
			description: label.description_name,
			translations: label.description,
			baselineDates: [this.cardData.baseline.start, this.cardData.baseline.end],
		},
	};
}

function closeSetMonthlyGoalModal(reload) {
	this.monthlyGoalModalData = null;

	if (reload) {
		this.cardData = null;
		this.fetchData();
	}
}

async function fetchData() {
	this.state = REQUEST_STATE.LOADING;

	// fetch the card data
	try {
		const { data } = await this.$api.getTrackedItemCard(this.item.label_id);
		this.cardData = data;
		this.state = REQUEST_STATE.SUCCESS;
	} catch (error) {
		if (error.response?.status === STATUS_CODE.MISSING_DATA) this.state = REQUEST_STATE.MISSING_DATA;
		else this.state = REQUEST_STATE.ERROR;
	} finally {
		this.$emit("notifyLoaded");
	}

	this.fetchTrashevents();
}

// eslint-disable-next-line complexity
async function fetchTrashevents() {
	if (this.cardData) {
		// fetch the example images
		const LIMIT = 7,
			dvDate = this.$moment(this.activeOutlet.outlet_data_validity, "YYYY-MM-DD"),
			trackingEnd = this.cardData.tracked_label.end_date
				? this.$moment(this.cardData.tracked_label.end_date, "YYYY-MM-DD")
				: undefined,
			untilDate = trackingEnd && trackingEnd < dvDate ? trackingEnd : dvDate,
			request = {
				outlet_id: this.activeOutlet.outlet_id,
				start_date: this.$moment(this.cardData.tracked_label.start_date, "YYYY-MM-DD").format("DD/MM/YYYY"),
				end_date: untilDate.format("DD/MM/YYYY"),
				type: "all",
				label_id: this.cardData.label_id,
				limit: LIMIT,
				order_by: "timestamp",
				order_direction: "desc",
			};

		try {
			const response = await TrashEventsService.getMyGoalsTrashEventItems(request);
			if (response.status === STATUS_CODE.SUCCESS) this.trashEvents = response.data;
			else this.trashEvents = [];
		} catch {
			this.trashEvents = [];
		}
	} else this.trashEvents = [];

	this.eventsLoading = false;
}

function formatDate(value, format) {
	return moment(value).format(format);
}

function toggleSeeMore() {
	this.seeMoreToggled = !this.seeMoreToggled;
}

async function stopTrackingLabel(labelId, endDate) {
	try {
		await SettingsService.setTrackedLabelDates({
			label: labelId,
			endDate: moment(endDate).format("YYYY-MM-DD"),
		});
		this.getMe();
		this.$eventBus.$emit("reloadTrackedItemsData");
		this.$emit("close");
	} catch (error) {
		this.$swal({
			text: error.message,
			customClass: "swal_modal",
		});
	}
}

function updateNote(note) {
	this.$set(this.cardData.tracked_label, "notes", note);
}
